import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAntenneSuggestions, rattacherContacts, getUploadsUrl } from '../services/api';
import { usePerm } from '../lib/perms';
import ConfirmModal from './ConfirmModal';

const QUALIF_LABELS = {
  antenne_principale: 'Antenne actuelle',
  antenne_binome: 'Binôme',
  historique_antenne: 'Ancienne antenne',
  'profil_intéressant': 'Profil intéressant',
};

// Situation actuelle du contact : c'est ce qui dit ce que le rattachement va changer.
const situation = (c) => {
  if (!c.antenne_id) return { label: 'Sans antenne', cls: 'sugg-etat-libre' };
  if (c.antenne_actuelle_supprimee) {
    return { label: `Antenne supprimée${c.antenne_actuelle_ville ? ` (${c.antenne_actuelle_ville})` : ''}`, cls: 'sugg-etat-libre' };
  }
  return { label: `Rattaché à ${c.antenne_actuelle_ville || `l'antenne #${c.antenne_id}`}`, cls: 'sugg-etat-autre' };
};

// Rattacher un contact d'une autre antenne ACTIVE le lui retire : on le dit avant.
const deplace = (c) => c.antenne_id && !c.antenne_actuelle_supprimee;

/**
 * Suggestions de rattachement sur la fiche antenne : contacts de la même ville
 * qui ne sont pas rattachés à cette antenne. Volontairement séparé de la liste
 * des contacts rattachés (autre carte, autre couleur).
 *
 * Props : antenneId, ville, onRattache() (recharger la fiche)
 */
export default function SuggestionsRattachement({ antenneId, ville, onRattache }) {
  const peutRattacher = usePerm('contacts.edit');
  const [items, setItems] = useState([]);
  const [selection, setSelection] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // ids à rattacher

  const charger = useCallback(async () => {
    try {
      setItems(await getAntenneSuggestions(antenneId));
      setSelection([]);
    } catch (e) {
      setItems([]);
    }
  }, [antenneId]);

  useEffect(() => { charger(); }, [charger]);

  if (items.length === 0) return null;

  const toggle = (id) =>
    setSelection((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  const tous = selection.length === items.length;

  const executer = async (ids) => {
    setBusy(true);
    try {
      const r = await rattacherContacts(antenneId, ids);
      toast.success(`${r?.rattaches ?? ids.length} contact(s) rattaché(s) à ${ville}`);
      setConfirmation(null);
      await charger();
      onRattache?.();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erreur lors du rattachement');
    } finally {
      setBusy(false);
    }
  };

  // Confirmation seulement si l'opération retire des contacts à une autre antenne active.
  const demander = (ids) => {
    const aDeplacer = items.filter((c) => ids.includes(c.id) && deplace(c));
    if (aDeplacer.length > 0) setConfirmation(ids);
    else executer(ids);
  };

  const nbDeplaces = confirmation ? items.filter((c) => confirmation.includes(c.id) && deplace(c)).length : 0;

  return (
    <section className="detail-card suggestions-section">
      <h2 className="section-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        Suggestions : contacts à {ville} non rattachés ({items.length})
      </h2>
      <p className="sugg-intro">
        Ces contacts ont {ville} pour ville mais ne sont pas rattachés à cette antenne.
        Ils n'apparaissent donc pas dans la liste des contacts de l'antenne.
      </p>

      {peutRattacher && (
        <div className="sugg-toolbar">
          <label className="sugg-check">
            <input type="checkbox" checked={tous} onChange={() => setSelection(tous ? [] : items.map((c) => c.id))} />
            Tout sélectionner
          </label>
          <button
            className="btn btn-primary btn-sm"
            disabled={selection.length === 0 || busy}
            onClick={() => demander(selection)}
          >
            Rattacher la sélection ({selection.length})
          </button>
        </div>
      )}

      <ul className="sugg-list">
        {items.map((c) => {
          const s = situation(c);
          const avatar = c.photo
            ? getUploadsUrl(c.photo)
            : c.civilite === 'm' ? '/img/avatar-homme.svg' : '/img/avatar-femme.svg';
          return (
            <li key={c.id} className={`sugg-item${selection.includes(c.id) ? ' is-selected' : ''}`}>
              {peutRattacher && (
                <input
                  type="checkbox"
                  checked={selection.includes(c.id)}
                  onChange={() => toggle(c.id)}
                  aria-label={`Sélectionner ${c.prenom || ''} ${c.nom || ''}`}
                />
              )}
              <img className="sugg-avatar" src={avatar} alt="" />
              <div className="sugg-info">
                <Link to={`/contacts/${c.id}`} className="sugg-name">
                  {c.prenom} {c.nom}
                </Link>
                <div className="sugg-meta">
                  <span className="sugg-qualif">{QUALIF_LABELS[c.qualification] || c.qualification || 'Sans type'}</span>
                  <span className={`sugg-etat ${s.cls}`}>{s.label}</span>
                  {c.ne_plus_contacter && <span className="sugg-etat sugg-etat-autre">Ne plus contacter</span>}
                </div>
              </div>
              {peutRattacher && (
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => demander([c.id])}>
                  Rattacher
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {confirmation && (
        <ConfirmModal
          titre="Déplacer des contacts ?"
          message={`${nbDeplaces} contact(s) sélectionné(s) sont déjà rattachés à une autre antenne active.`}
          detail={`Les rattacher à ${ville} les retirera de leur antenne actuelle. Le changement est noté dans l'historique de chaque contact.`}
          libelleConfirmer={`Rattacher ${confirmation.length} contact(s)`}
          busy={busy}
          onConfirm={() => executer(confirmation)}
          onClose={() => !busy && setConfirmation(null)}
        />
      )}
    </section>
  );
}
